import {
  ActionIcon,
  Box,
  Button,
  Group,
  NumberInput,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { PiDotsSixVertical, PiPlus, PiX } from 'react-icons/pi';
import { formatPrice } from '@/lib/helpers';
import {
  useSelectedServices,
  useManualInvoiceActions,
  SelectedService,
} from '@/models/manual-invoice.store';
import { TService } from '@/models/services';
import ServiceSelector from '@/components/service-selector/ServiceSelector';

interface DraggableServiceRowProps {
  service: SelectedService;
  index: number;
  onRemove: (serviceId: number) => void;
  onQuantityChange: (serviceId: number, quantity: number) => void;
}

function DraggableServiceRow({
  service,
  index,
  onRemove,
  onQuantityChange,
}: DraggableServiceRowProps) {
  const amount = service.price * service.quantity;

  return (
    <Draggable draggableId={String(service.id)} index={index}>
      {(provided) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          p="xs"
          withBorder
          bg="gray.0"
          mb="xs"
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap" style={{ flex: 1 }}>
              <Box
                {...provided.dragHandleProps}
                style={{ cursor: 'grab', touchAction: 'none' }}
              >
                <PiDotsSixVertical
                  size={16}
                  color="var(--mantine-color-gray-5)"
                />
              </Box>

              <Stack gap={2} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  {service.name}
                </Text>

                {service.children.length > 0 && (
                  <Stack gap={0} ml="md">
                    {service.children.map((child) => (
                      <Text key={child.id} size="xs" c="dimmed">
                        - {child.name}
                      </Text>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Group>

            <Group gap="md" wrap="nowrap">
              <NumberInput
                size="xs"
                value={service.quantity}
                onChange={(val) =>
                  onQuantityChange(service.serviceId, Number(val) || 1)
                }
                min={1}
                max={999}
                w={60}
                styles={{ input: { textAlign: 'center' } }}
              />

              <Text size="sm" c="dimmed" w={80} ta="right">
                {formatPrice(service.price, service.currency)}
              </Text>

              <Text size="sm" fw={500} w={100} ta="right">
                {formatPrice(amount, service.currency)}
              </Text>

              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => onRemove(service.serviceId)}
              >
                <PiX size={14} />
              </ActionIcon>
            </Group>
          </Group>
        </Paper>
      )}
    </Draggable>
  );
}

export default function ServicesSection() {
  const services = useSelectedServices();
  const { addService, removeService, updateServiceQuantity, reorderServices } =
    useManualInvoiceActions();
  const [selectorOpen, setSelectorOpen] = useState(false);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    reorderServices(source.index, destination.index);
  };

  const handleServiceSelect = (service: TService) => {
    addService(service);
    setSelectorOpen(false);
  };

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="md">
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
          Services
        </Text>

        <Button
          size="xs"
          variant="light"
          leftSection={<PiPlus size={14} />}
          onClick={() => setSelectorOpen(true)}
        >
          Add Service
        </Button>
      </Group>

      {services.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          No services added. Click "Add Service" to begin.
        </Text>
      ) : (
        <>
          <Group justify="flex-end" mb="xs" pr={38}>
            <Text size="xs" c="dimmed" w={60} ta="center">
              Qty
            </Text>
            <Text size="xs" c="dimmed" w={80} ta="right">
              Rate
            </Text>
            <Text size="xs" c="dimmed" w={100} ta="right">
              Amount
            </Text>
          </Group>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="manual-invoice-services">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {services.map((service, index) => (
                    <DraggableServiceRow
                      key={service.id}
                      service={service}
                      index={index}
                      onRemove={removeService}
                      onQuantityChange={updateServiceQuantity}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}

      <ServiceSelector
        opened={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSelect={handleServiceSelect}
        exclude={services.map((s) => s.serviceId)}
      />
    </Paper>
  );
}
